import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  LayoutDashboard,
  Bell,
  Wrench,
  FileText,
  Layers,
  Shield,
  Users,
  Settings,
  DatabaseBackup,
  Activity,
  UserCheck,
  Palette,
  LogOut,
  Cpu,
  Mail,
  SlidersHorizontal,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  type LucideIcon,
  Plug,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useAlertCount } from '../../hooks/useAlertCount';
import { useThemeStore } from '../../stores/themeStore';
import { SidebarSection } from './SidebarSection';
import { cn } from '../../lib/cn';

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
  badge?: number;
  end?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
  requiredRole?: 'master';
}

const ROLE_LABELS: Record<string, string> = {
  master: 'Master',
  account_manager: 'Account Manager',
  technician: 'Technician',
  viewer: 'Viewer',
};

const ROLE_STYLES: Record<string, string> = {
  master: 'bg-danger/15 text-danger',
  account_manager: 'bg-info/15 text-info',
  technician: 'bg-success/15 text-success',
  viewer: 'bg-white/10 text-slate-400',
};

export function Sidebar() {
  const { user, isMaster, isAccountManager, logout } = useAuth();
  const alertCount = useAlertCount();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const theme = useThemeStore((s) => s.theme);

  const sections: NavSection[] = [
    {
      title: 'Main',
      items: [
        { label: 'Fleet', to: '/', icon: LayoutDashboard },
        { label: 'Alerts', to: '/alerts', icon: Bell, badge: alertCount },
        { label: 'Maintenance', to: '/maintenance', icon: Wrench },
        { label: 'Reports', to: '/reports', icon: FileText },
      ],
    },
    {
      title: 'Management',
      items: [
        { label: 'Groups', to: '/groups', icon: Layers },
        ...(isMaster
          ? [
              { label: 'Accounts', to: '/admin/accounts', icon: Shield, end: true },
            ]
          : []),
        ...(isAccountManager || isMaster
          ? [
              { label: 'Users', to: '/admin/users', icon: Users },
              { label: 'User Site Access', to: '/user-site-access', icon: UserCheck },
              ...(user?.accountId
                ? [{ label: 'Theme', to: `/admin/accounts/${user.accountId}/theme`, icon: Palette }]
                : []),
            ]
          : []),
        ...(isMaster
          ? [
              { label: 'Backfills', to: '/admin/backfills', icon: DatabaseBackup },
              // ENER-118: WAPI Compare hosts both single-site + fleet modes
              // via an in-page toggle, so one nav entry covers both.
              { label: 'WAPI Compare', to: '/admin/wapi-compare/1775625', icon: Activity },
            ]
          : []),
      ],
    },
    ...(isMaster
      ? [{
          title: 'Admin',
          items: [
            { label: 'Workers', to: '/admin/workers', icon: Cpu },
            { label: 'Contact Messages', to: '/admin/contact-messages', icon: Mail },
            { label: 'OEM Connections', to: '/admin/oem-connections', icon: Plug },
            { label: 'System Config', to: '/admin/settings', icon: SlidersHorizontal },
          ],
        }]
      : []),
    {
      title: 'User',
      items: [{ label: 'Settings', to: '/settings', icon: Settings }],
    },
  ];

  const filteredSections = sections.filter(
    (s) => !s.requiredRole || (s.requiredRole === 'master' && isMaster),
  );

  const navContent = (
    <>
      {/* Logo / Company name */}
      <div className="px-6 pt-6 pb-4">
        <div
          className="cursor-pointer"
          onClick={() => { navigate('/'); setMobileOpen(false); }}
        >
          <img src="/logo.png" alt="Enerlyze" className="h-10 w-auto" />
          {theme.companyName && theme.companyName !== 'ENERLYZE' && (
            <p className="text-sm text-muted mt-1.5 truncate">{theme.companyName}</p>
          )}
        </div>
        {user && (
          <div className="mt-3 pt-3 border-t border-white/5">
            <p className="text-xs text-text truncate">{user.email}</p>
            <span className={`inline-flex mt-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full ${ROLE_STYLES[user.role] ?? 'bg-white/10 text-slate-400'}`}>
              {ROLE_LABELS[user.role] ?? user.role}
            </span>
          </div>
        )}
      </div>

      {/* Nav sections */}
      <nav className="flex-1 px-3 space-y-6 overflow-y-auto" aria-label="Main navigation">
        {filteredSections.map((section) => (
          <SidebarSection
            key={section.title}
            title={section.title}
            items={section.items}
            onItemClick={() => setMobileOpen(false)}
          />
        ))}
      </nav>

      {/* Logout + Collapse — pinned to bottom */}
      <div className="mt-auto px-3 py-4 border-t border-white/10 space-y-1">
        <button
          onClick={logout}
          className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-muted hover:text-text hover:bg-white/[0.05] rounded-lg transition-colors"
        >
          <LogOut className="w-4 h-4 flex-shrink-0" />
          {!collapsed && <span>Logout</span>}
        </button>
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-3 w-full px-3 py-2.5 text-sm text-muted hover:text-text hover:bg-white/[0.05] rounded-lg transition-colors"
        >
          {collapsed ? <PanelLeftOpen className="w-4 h-4 flex-shrink-0" /> : <PanelLeftClose className="w-4 h-4 flex-shrink-0" />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </>
  );

  const collapsedContent = (
    <>
      {/* Collapsed logo */}
      <div className="px-3 py-6 flex justify-center">
        <div
          className="cursor-pointer"
          onClick={() => { navigate('/'); }}
        >
          <img src="/logo-icon.png" alt="Enerlyze" className="h-6 w-auto" />
        </div>
      </div>

      {/* Collapsed nav — icons only */}
      <nav className="flex-1 px-2 space-y-1 overflow-y-auto" aria-label="Main navigation">
        {filteredSections.flatMap((section) =>
          section.items.map((item) => (
            <CollapsedNavItem key={item.to} item={item} />
          )),
        )}
      </nav>

      {/* Logout + Expand — pinned to bottom */}
      <div className="mt-auto px-2 py-4 border-t border-white/10 space-y-1">
        <button
          onClick={logout}
          className="flex items-center justify-center w-full p-2.5 text-muted hover:text-text hover:bg-white/[0.05] rounded-lg transition-colors"
          title="Logout"
        >
          <LogOut className="w-4 h-4" />
        </button>
        <button
          onClick={() => setCollapsed(false)}
          className="flex items-center justify-center w-full p-2.5 text-muted hover:text-text hover:bg-white/[0.05] rounded-lg transition-colors"
          title="Expand sidebar"
        >
          <PanelLeftOpen className="w-4 h-4" />
        </button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile hamburger */}
      <button
        onClick={() => setMobileOpen(true)}
        className="fixed top-4 left-4 z-50 lg:hidden bg-sidebar border border-white/10 rounded-lg p-2 text-text"
        aria-label="Open menu"
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/60 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar - mobile (always full width) */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-[280px] bg-sidebar flex flex-col lg:hidden transition-transform duration-300 relative overflow-hidden',
          mobileOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <button
          onClick={() => setMobileOpen(false)}
          className="absolute top-4 right-4 text-muted hover:text-text"
          aria-label="Close menu"
        >
          <X className="w-5 h-5" />
        </button>
        {navContent}
      </aside>

      {/* Sidebar - desktop (fixed position — never scrolls with content) */}
      <aside
        className={cn(
          'hidden lg:flex bg-sidebar flex-col h-screen fixed top-0 left-0 z-30 border-r border-white/5 transition-all duration-200',
          collapsed ? 'w-[64px]' : 'w-[280px]',
        )}
      >
        {collapsed ? collapsedContent : navContent}
      </aside>
      {/* Spacer — pushes main content to the right of the fixed sidebar */}
      <div className={cn('hidden lg:block flex-shrink-0 transition-all duration-200', collapsed ? 'w-[64px]' : 'w-[280px]')} />
    </>
  );
}

// ── Collapsed nav item (icon only with tooltip) ──────────────────────────────

function CollapsedNavItem({ item }: { item: NavItem }) {
  const navigate = useNavigate();
  const Icon = item.icon;

  return (
    <button
      onClick={() => navigate(item.to)}
      className="flex items-center justify-center w-full p-2.5 rounded-lg text-muted hover:text-text hover:bg-white/[0.05] transition-colors relative"
      title={item.label}
    >
      <Icon className="w-4 h-4" />
      {item.badge != null && item.badge > 0 && (
        <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-danger" />
      )}
    </button>
  );
}
